import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Stepper,
    Step,
    StepLabel,
    Paper,
    Chip,
    InputAdornment,
    Alert,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { CreateShipmentDevice, DeviceType } from '../lps.types';
import { createShipment } from '../lps.api';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const STEPS = ['Shipment Info', 'Register Devices', 'Review & Submit'];

const TYPE_LABELS: Record<DeviceType, string> = {
    anchor: 'Anchor',
    tracker: 'Tracker',
    gateway: 'Gateway',
};

const LpsReceiveShipmentDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Step 1: Shipment info
    const [supplierName, setSupplierName] = useState('');
    const [poNumber, setPoNumber] = useState('');
    const [receivedAt, setReceivedAt] = useState(new Date().toISOString().split('T')[0]);
    const [shipmentNotes, setShipmentNotes] = useState('');

    // Step 2: Devices
    const [devices, setDevices] = useState<CreateShipmentDevice[]>([]);
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkSerials, setBulkSerials] = useState('');
    const [bulkType, setBulkType] = useState<DeviceType>('anchor');
    const [bulkModel, setBulkModel] = useState('');

    const handleAddDevice = () => {
        setDevices([
            ...devices,
            { serial_number: '', device_type: 'anchor', model: '', unit_cost_cents: 0 },
        ]);
    };

    const handleRemoveDevice = (idx: number) => {
        setDevices(devices.filter((_, i) => i !== idx));
    };

    const handleDeviceChange = (idx: number, field: keyof CreateShipmentDevice, value: any) => {
        const updated = [...devices];
        updated[idx] = { ...updated[idx], [field]: value };
        setDevices(updated);
    };

    const handleBulkAdd = () => {
        const serials = bulkSerials
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);

        if (serials.length === 0) return;

        const newDevices: CreateShipmentDevice[] = serials.map((sn) => ({
            serial_number: sn,
            device_type: bulkType,
            model: bulkModel || undefined,
        }));

        setDevices([...devices, ...newDevices]);
        setBulkSerials('');
        setBulkMode(false);
    };

    const validateStep1 = () => {
        if (!supplierName.trim()) {
            setError('Supplier name is required');
            return false;
        }
        if (!receivedAt) {
            setError('Received date is required');
            return false;
        }
        setError('');
        return true;
    };

    const validateStep2 = () => {
        if (devices.length === 0) {
            setError('Add at least one device');
            return false;
        }
        const serials = devices.map((d) => d.serial_number.trim().toLowerCase());
        const duplicates = serials.filter((s, i) => s && serials.indexOf(s) !== i);
        if (duplicates.length > 0) {
            setError(`Duplicate serial numbers: ${[...new Set(duplicates)].join(', ')}`);
            return false;
        }
        if (devices.some((d) => !d.serial_number.trim())) {
            setError('All devices must have a serial number');
            return false;
        }
        setError('');
        return true;
    };

    const handleNext = () => {
        if (activeStep === 0 && !validateStep1()) return;
        if (activeStep === 1 && !validateStep2()) return;
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setError('');
        setActiveStep((prev) => prev - 1);
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            await createShipment({
                supplier_name: supplierName,
                po_number: poNumber || undefined,
                received_at: receivedAt,
                notes: shipmentNotes || undefined,
                devices: devices.map((d) => ({
                    ...d,
                    serial_number: d.serial_number.trim(),
                    unit_cost_cents: d.unit_cost_cents || undefined,
                    model: d.model || undefined,
                })),
            });
            onSuccess();
            handleReset();
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to create shipment';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setActiveStep(0);
        setSupplierName('');
        setPoNumber('');
        setReceivedAt(new Date().toISOString().split('T')[0]);
        setShipmentNotes('');
        setDevices([]);
        setBulkMode(false);
        setBulkSerials('');
        setError('');
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const formatCost = (cents: number) => (cents / 100).toFixed(2);
    const totalCost = devices.reduce((sum, d) => sum + (d.unit_cost_cents || 0), 0);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Receive Shipment
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Step 1: Shipment Info */}
                {activeStep === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            label="Supplier Name *"
                            value={supplierName}
                            onChange={(e) => setSupplierName(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="e.g. Pozyx, DecaWave"
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="PO Number"
                                value={poNumber}
                                onChange={(e) => setPoNumber(e.target.value)}
                                sx={{ flex: 1 }}
                                size="small"
                                placeholder="Purchase order reference"
                            />
                            <TextField
                                label="Date Received *"
                                type="date"
                                value={receivedAt}
                                onChange={(e) => setReceivedAt(e.target.value)}
                                sx={{ flex: 1 }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                        <TextField
                            label="Notes"
                            value={shipmentNotes}
                            onChange={(e) => setShipmentNotes(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            placeholder="e.g. Partial shipment, remaining 10 trackers back-ordered"
                        />
                    </Box>
                )}

                {/* Step 2: Register Devices */}
                {activeStep === 1 && (
                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Devices ({devices.length})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ContentPasteIcon />}
                                    onClick={() => setBulkMode(!bulkMode)}
                                >
                                    Bulk Paste
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddDevice}
                                >
                                    Add Device
                                </Button>
                            </Box>
                        </Box>

                        {bulkMode && (
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'action.hover' }}
                            >
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                    Paste serial numbers (one per line)
                                </Typography>
                                <TextField
                                    multiline
                                    rows={4}
                                    fullWidth
                                    size="small"
                                    value={bulkSerials}
                                    onChange={(e) => setBulkSerials(e.target.value)}
                                    placeholder={'SN-001\nSN-002\nSN-003'}
                                    sx={{ mb: 1.5 }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={bulkType}
                                            onChange={(e) =>
                                                setBulkType(e.target.value as DeviceType)
                                            }
                                            label="Type"
                                        >
                                            <MenuItem value="anchor">Anchor</MenuItem>
                                            <MenuItem value="tracker">Tracker</MenuItem>
                                            <MenuItem value="gateway">Gateway</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        label="Model"
                                        value={bulkModel}
                                        onChange={(e) => setBulkModel(e.target.value)}
                                        placeholder="e.g. DWM1001-DEV"
                                    />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleBulkAdd}
                                    >
                                        Add {bulkSerials.split('\n').filter((s) => s.trim()).length}{' '}
                                        Devices
                                    </Button>
                                </Box>
                            </Paper>
                        )}

                        {devices.length === 0 && !bulkMode && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    borderStyle: 'dashed',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    No devices added yet. Click &ldquo;Add Device&rdquo; or
                                    &ldquo;Bulk Paste&rdquo; to register hardware.
                                </Typography>
                            </Paper>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {devices.map((device, idx) => (
                                <Paper
                                    key={idx}
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        display: 'flex',
                                        gap: 1.5,
                                        alignItems: 'center',
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        label="Serial Number *"
                                        value={device.serial_number}
                                        onChange={(e) =>
                                            handleDeviceChange(idx, 'serial_number', e.target.value)
                                        }
                                        sx={{ flex: 2 }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={device.device_type}
                                            onChange={(e) =>
                                                handleDeviceChange(
                                                    idx,
                                                    'device_type',
                                                    e.target.value,
                                                )
                                            }
                                            label="Type"
                                        >
                                            <MenuItem value="anchor">Anchor</MenuItem>
                                            <MenuItem value="tracker">Tracker</MenuItem>
                                            <MenuItem value="gateway">Gateway</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        label="Model"
                                        value={device.model || ''}
                                        onChange={(e) =>
                                            handleDeviceChange(idx, 'model', e.target.value)
                                        }
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Unit Cost"
                                        type="number"
                                        value={
                                            device.unit_cost_cents
                                                ? (device.unit_cost_cents / 100).toFixed(2)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            handleDeviceChange(
                                                idx,
                                                'unit_cost_cents',
                                                Math.round(parseFloat(e.target.value || '0') * 100),
                                            )
                                        }
                                        sx={{ width: 110 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">$</InputAdornment>
                                            ),
                                        }}
                                    />
                                    <IconButton
                                        onClick={() => handleRemoveDevice(idx)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Step 3: Review */}
                {activeStep === 2 && (
                    <Box>
                        <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                Shipment Details
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                <ReviewItem label="Supplier" value={supplierName} />
                                <ReviewItem label="PO Number" value={poNumber || '--'} />
                                <ReviewItem label="Received" value={receivedAt} />
                                <ReviewItem
                                    label="Total Cost"
                                    value={totalCost > 0 ? `$${formatCost(totalCost)}` : '--'}
                                />
                            </Box>
                            {shipmentNotes && (
                                <Box sx={{ mt: 1.5 }}>
                                    <ReviewItem label="Notes" value={shipmentNotes} />
                                </Box>
                            )}
                        </Paper>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Devices ({devices.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            {(['anchor', 'tracker', 'gateway'] as DeviceType[]).map((type) => {
                                const count = devices.filter((d) => d.device_type === type).length;
                                if (count === 0) return null;
                                return (
                                    <Chip
                                        key={type}
                                        label={`${count} ${TYPE_LABELS[type]}${
                                            count > 1 ? 's' : ''
                                        }`}
                                        size="small"
                                        variant="outlined"
                                    />
                                );
                            })}
                        </Box>
                        <Paper
                            variant="outlined"
                            sx={{ maxHeight: 200, overflow: 'auto', borderRadius: 1.5 }}
                        >
                            {devices.map((d, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        px: 2,
                                        py: 0.75,
                                        borderBottom:
                                            idx < devices.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                                    >
                                        {d.serial_number}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {TYPE_LABELS[d.device_type]}
                                        </Typography>
                                        {d.model && (
                                            <Typography variant="body2" color="text.secondary">
                                                {d.model}
                                            </Typography>
                                        )}
                                        {(d.unit_cost_cents || 0) > 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                ${formatCost(d.unit_cost_cents!)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Paper>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose}>Cancel</Button>
                <Box sx={{ flex: 1 }} />
                {activeStep > 0 && (
                    <Button onClick={handleBack} disabled={saving}>
                        Back
                    </Button>
                )}
                {activeStep < STEPS.length - 1 ? (
                    <Button variant="contained" onClick={handleNext}>
                        Next
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={saving}
                        color="primary"
                    >
                        {saving ? (
                            <CircularProgress size={20} />
                        ) : (
                            `Create Shipment (${devices.length} devices)`
                        )}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

const ReviewItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
        </Typography>
    </Box>
);

export default LpsReceiveShipmentDialog;
